import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { TRACK_STATS, EQUITY, EQUITY_MONTHS, TRADE_BREAKDOWN } from "../mock/data";
import { Wrap, SectionHead, Note } from "./Section";
import { useReveal } from "../hooks/useReveal";
import CountUp from "./CountUp";
import api from "../lib/api";

function EquityCurve({ data = EQUITY }) {
  const w = 800, h = 240, pad = 8;
  const max = Math.max(...data);
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2);
    const y = h - pad - (v / max) * (h - pad * 2);
    return [x, y];
  });
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${h} L${pts[0][0].toFixed(1)},${h} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" preserveAspectRatio="none">
      <defs>
        <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(196,245,66,0.28)" />
          <stop offset="100%" stopColor="rgba(196,245,66,0)" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((g) => (
        <line key={g} x1="0" y1={h * g} x2={w} y2={h * g} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
      ))}
      <path d={area} fill="url(#eq)" />
      <path d={line} fill="none" stroke="#c4f542" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill="#c4f542" />
      ))}
    </svg>
  );
}

export default function TrackRecord() {
  const [ref, inView] = useReveal();
  const [data, setData] = useState({
    stats: TRACK_STATS, equity: EQUITY, equity_months: EQUITY_MONTHS,
    breakdown: TRADE_BREAKDOWN, followers: 1240,
  });

  useEffect(() => {
    api.get("/track-record").then((r) => {
      if (r.data) setData((d) => ({ ...d, ...r.data }));
    }).catch(() => {});
  }, []);

  return (
    <Wrap id="performance">
      <SectionHead
        tag="Snapshot · 16/09/2026"
        title="A live track record,"
        highlight="publicly verifiable"
        desc="Trading live since November 2024 and available on CopyX since September 2025. Every figure below comes from the public Myfxbook account and is copied here by hand — check the source for the current numbers."
        link={
          <a href="https://www.myfxbook.com/members/SonicExperts/sonic-ai/12076857" target="_blank" rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 btn-ghost px-4 py-2.5 rounded-lg">
            Verify on Myfxbook <ExternalLink size={13} />
          </a>
        }
      />

      <div className="mt-12 grid grid-cols-2 md:grid-cols-6 border border-white/[0.07] rounded-2xl overflow-hidden">
        {data.stats.map((s, i) => (
          <div key={i} className="p-5 border-b md:border-b-0 border-r border-white/[0.07] last:border-r-0 [&:nth-child(2)]:border-r-0 md:[&:nth-child(2)]:border-r">
            <div className="label text-[var(--muted)] text-[0.6rem] mb-2">{s.label}</div>
            <CountUp value={s.value} className="display text-accent text-2xl block" />
          </div>
        ))}
      </div>

      <div ref={ref} className={`reveal ${inView ? "in" : ""} mt-6 card rounded-2xl p-6`}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
          <div className="label text-[var(--muted)]">Account-level equity curve · reconstruction of the snapshot</div>
          <div className="mono text-accent text-sm">+{(data.equity[data.equity.length - 1] || 29.23).toFixed(2)}% cumulative</div>
        </div>
        <EquityCurve data={data.equity} />
        <div className="flex justify-between mt-3">
          {data.equity_months.map((m) => (
            <span key={m} className="mono text-[10px] text-[var(--muted)]">{m}</span>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        {data.breakdown.map((b, i) => (
          <div key={i} className="card rounded-2xl p-5">
            <div className="label text-[var(--muted)] text-[0.6rem] mb-2">{b.label}</div>
            <div className="text-lg font-semibold text-[var(--text)]">{b.value}</div>
          </div>
        ))}
      </div>

      <Note>
        Max drawdown is measured at account level, and amplification does not change it — a 0.86% account
        drawdown is 0.86% whether the structure is 12× or 24×. Figures are a manually maintained snapshot
        dated 16/09/2026. Past performance is not indicative of future results.
      </Note>

      <div className="mt-10 card rounded-2xl p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div>
          <div className="label text-[var(--muted)] mb-2">Followers on CopyX · 14/09/2026</div>
          <CountUp value={String(data.followers)} className="display text-accent text-5xl block" />
        </div>
        <p className="text-[13px] text-[var(--muted)] max-w-md leading-relaxed">
          Accounts following the Sonic AI strategy through CopyX at the date shown. A follower count is a
          measure of adoption, not of safety or of expected return.
        </p>
      </div>
    </Wrap>
  );
}
